import { Box, useMediaQuery, useTheme } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { Card, Dialog } from '../../components';

import CardHistoryButton from './card-history-button';
import { getRefValue } from '../../utils/get-ref-value';
import CardHistoryIndicator from './card-history-indicator';
import { useWindowSize } from '../../hooks/use-window-size';
import { urlParam } from '../../utils/url-param';
import FabControlled from '../../components/fab/fab-controlled';

type Props = {
  open: boolean;
  onClose: any;
  carHistoryImages?: any;
};

export default function CardHistory(props: Props) {
  const { open, onClose, carHistoryImages } = props;
  const { height } = useWindowSize();

  const carHistoryTitle = urlParam('carHistoryTitle');

  const theme: any = useTheme();
  // const { width } = useWindowSize();
  const { zIndex }: any = useTheme();

  const isLandscape = useMediaQuery('(orientation: landscape)');
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('lg'));

  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [offsetX, setOffsetX] = React.useState(0);

  const containerRef = React.useRef<HTMLDivElement>(null);

  const handleSlide = (slideAction: string) => {
    const imageListLength = carHistoryImages.length;
    const containerEl = getRefValue(containerRef);
    const containerWidth = containerEl.offsetWidth;

    setCurrentIndex((prevIndex: number) => {
      let index = prevIndex;

      if (slideAction === 'next') {
        index = (prevIndex + 1) % imageListLength;
      } else if (slideAction === 'back') {
        index = (prevIndex - 1 + imageListLength) % imageListLength;
      }

      setOffsetX(-(containerWidth * index));

      return index;
    });
  };

  const handleSelectSlide = (index: number) => {
    const containerEl = getRefValue(containerRef);
    const containerWidth = containerEl.offsetWidth;

    setCurrentIndex(index);

    setOffsetX(-(containerWidth * index));
  };

  const renderClose = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        alignSelf: 'end',
        [theme.breakpoints.down('lg')]: {
          margin: '0',
        },
        margin: `-0.8rem -0.8rem ${carHistoryTitle ? '0' : '1.5rem'} 0`,
        zIndex: zIndex.modal + 1,
      }}
    >
      <FabControlled Icon={CloseRoundedIcon} onClick={onClose} />
    </Box>
  );

  const renderHeader = (
    <>
      {renderClose}

      {carHistoryTitle && (
        <Box sx={{ pb: { xl: 2, xs: 0 } }}>
          <Typography
            align="center"
            sx={{
              color: '#000',
              fontWeight: '700',
              textTransform: 'capitalize',
            }}
            variant="h5"
          >
            {carHistoryTitle}
          </Typography>
        </Box>
      )}
    </>
  );

  const renderContainer = (
    <>
      <Box
        ref={containerRef}
        sx={{
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          maxWidth: '800px',
        }}
      >
        {carHistoryImages?.length === 1 ? (
          <Box
            component={'img'}
            key={carHistoryImages[0]?.name}
            src={carHistoryImages[0]?.src}
            sx={{
              borderRadius: theme.spacing(1),
              height: '100%',
              maxHeight: '100%',
              maxWidth: '100%',
              objectFit: 'cover',
              width: '100%',
              padding: 1,
            }}
          />
        ) : (
          carHistoryImages?.map((image: any, index: number) => (
            <Box
              component={'img'}
              key={image.name}
              src={image.src}
              sx={{
                borderRadius: theme.spacing(1),
                height: '100%',
                maxHeight: '100%',
                maxWidth: '100%',
                objectFit: 'cover',
                width: '100%',
                padding: 1,
                flexGrow: 0,
                flexShrink: 0,
                opacity: currentIndex === index ? 1 : 0.4,
                transition: 'opacity 500ms ease-in-out',
                transform: `translate(${offsetX}px)`,
              }}
            />
          ))
        )}

        {carHistoryImages?.length !== 1 && (
          <CardHistoryButton handleSlide={handleSlide} />
        )}
      </Box>
      {carHistoryImages?.length !== 1 && (
        <CardHistoryIndicator
          currentIndex={currentIndex}
          handleSelectSlide={handleSelectSlide}
          numberOfImages={carHistoryImages?.length || 0}
        />
      )}
    </>
  );

  const renderContent = (
    <CardContent sx={{ padding: { sm: 0 }, paddingBottom: '0 !important' }}>
      <Typography
        align="center"
        display="block"
        gutterBottom
        sx={{
          color: '#000',
          fontWeight: 500,
          textTransform: 'capitalize',
        }}
        variant="caption"
      >
        This query was taken from the{' '}
        <Typography color={'blue'} display={'inline'}>
          carfax
        </Typography>{' '}
        site and its validity is related to this platform.
      </Typography>
    </CardContent>
  );

  const renderCard = (
    <Box
      sx={{
        [theme.breakpoints.down('lg')]: {
          width: isLandscape ? height * 0.5 : 'auto',
        },
        '&>div': {
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          flexDirection: 'column',
        },
      }}
    >
      <Card
        cardContainer={renderContainer}
        cardContent={renderContent}
        cardHeader={renderHeader}
      />
    </Box>
  );

  return (
    <Dialog
      disableRenderClose={isMobile && isLandscape}
      handleClose={onClose}
      open={Boolean(open)}
      renderContent={Boolean(open) && renderCard}
    />
  );
}
